import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowUpRight, Upload, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiErrorMessage } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = '.pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';

export default function CareersModal({ open, onOpenChange }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', role: '', message: '',
  });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error('CV is too large. Maximum 5 MB.');
      return;
    }
    setCv(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Name and email are required');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      if (form.phone) fd.append('phone', form.phone);
      if (form.role) fd.append('role', form.role);
      if (form.message) fd.append('message', form.message);
      if (cv) fd.append('cv', cv);
      await axios.post(`${API}/careers/apply`, fd);
      toast.success('Application received. HR will be in touch soon.');
      setForm({ name: '', email: '', phone: '', role: '', message: '' });
      setCv(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not submit application'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px] bg-[#F5F1E8] p-0 overflow-hidden">
        <div className="p-7 pb-2">
          <DialogTitle asChild>
            <h2 className="font-serif text-[26px] leading-tight text-[#0B3B2E]">Apply to join Vector FX</h2>
          </DialogTitle>
          <DialogDescription className="text-[#0E1F1A]/60 mt-1 text-[13px]">
            Tell us who you are, what you would like to do, and drop us your CV. HR will get back to you shortly.
          </DialogDescription>
        </div>
        <form onSubmit={submit} className="px-7 pb-7 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Full name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Row>
            <Row label="Email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Row>
            <Row label="Phone (optional)"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Row>
            <Row label="Role you are interested in">
              <Input value={form.role} onChange={(e) => set('role', e.target.value)} placeholder="e.g. Senior Backend Engineer" />
            </Row>
          </div>
          <Row label="A short note (optional)">
            <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={3}
              placeholder="Where you are today, what you're looking for, or a link to your portfolio."
              className="w-full rounded-md border border-[#0B3B2E]/20 bg-white p-2.5 text-[14px]" />
          </Row>

          <div className="space-y-1">
            <Label className="text-[12px] text-[#0E1F1A]/65">CV (PDF, DOC, DOCX or TXT · max 5 MB)</Label>
            {!cv ? (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 h-16 rounded-md border-2 border-dashed border-[#0B3B2E]/25 bg-white/60 text-[#0B3B2E]/70 hover:bg-white hover:border-[#0B3B2E]/50 transition-colors">
                <Upload className="w-4 h-4" />
                <span className="text-[13px] font-medium">Choose a file to upload</span>
              </button>
            ) : (
              <div className="flex items-center gap-3 rounded-md border border-[#0B3B2E]/20 bg-white p-3">
                <FileText className="w-5 h-5 text-[#0B3B2E]" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[#0B3B2E] truncate">{cv.name}</p>
                  <p className="text-[11px] text-[#0E1F1A]/55">{(cv.size / 1024).toFixed(0)} KB</p>
                </div>
                <button type="button" onClick={() => setCv(null)} className="text-[#0E1F1A]/50 hover:text-rose-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <input ref={fileRef} type="file" accept={ACCEPT} onChange={pickFile} className="hidden" />
          </div>

          <Button type="submit" disabled={busy}
            className="w-full rounded-full h-11 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] font-medium disabled:opacity-70">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending</>
              : <>Submit application <ArrowUpRight className="w-4 h-4 ml-1" /></>}
          </Button>
          <p className="text-center text-[11px] text-[#0E1F1A]/55">
            Your details go to HR at Vector FX. Automated email delivery to HR is being finalised.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[12px] text-[#0E1F1A]/65">{label}</Label>
      {children}
    </div>
  );
}
